import { asRpcError, num, numOrNull, secondary, useLiveQuery, useRpcAction, type ActionResult, type LiveState } from './rpc';
import { asRows, type Row } from './rows';

/**
 * Weekly driver settlements (fleet-driver-pay 0280/0281, design §1.5 and §7.2.4): what the agency, or a
 * truck owner, pays a company driver for a week: his load shares, minus his deductions, plus manual
 * adjustments, minus a deficit carried from an earlier week. Every read is INVOKER on the money RLS
 * (admin and accounting); every writer re-checks the permission key AND the money role on the server.
 *
 * `useDriverPayModes()`: who pays the drivers (the agency default and the per-carrier rows), the week cut,
 * and whether any settlement exists. That is what decides if the feature is "in use": the nav entry and the
 * list show only then, otherwise the explained empty state (design-direction rule 1). Carrier Logistics
 * (every carrier in `carrier` mode, no settlement) sees nothing new.
 */

export type DriverPayer = 'carrier' | 'agency' | 'owner';
export type SettlementPayer = 'agency' | 'owner';
export const DRIVER_SETTLEMENT_STATUSES = ['draft', 'final', 'canceled'] as const;
export type DriverSettlementStatus = (typeof DRIVER_SETTLEMENT_STATUSES)[number];
/** audit-e M3 (0342): `reimbursement` = a lumper the driver paid, from the load's accessorials (follows the load line). */
export type DriverSettlementLineKind = 'load' | 'reimbursement' | 'deduction' | 'adjustment' | 'balance_forward';

export interface DriverSettlement {
  id: string;
  driverId: string;
  driverName: string;
  payer: SettlementPayer;
  payerOwnerId: string | null;
  payerName: string | null;
  weekStart: string;
  number: string;
  status: DriverSettlementStatus;
  loadsTotal: number;
  deductionsTotal: number;
  adjustmentsTotal: number;
  balanceForwardTotal: number;
  netTotal: number;
  carriedToSettlementId: string | null;
  finalizedAt: string | null;
  canceledAt: string | null;
  cancelReason: string | null;
  paidOn: string | null;
  paidAt: string | null;
  paymentMethod: string | null;
  paymentReference: string | null;
}

export interface DriverSettlementLine {
  id: string;
  settlementId: string;
  lineNo: number;
  weekStart: string;
  kind: DriverSettlementLineKind;
  loadId: string | null;
  loadNumber: string | null;
  carrierId: string | null;
  truckId: string | null;
  deductionId: string | null;
  fromSettlementId: string | null;
  description: string;
  amount: number;
  originalAmount: number | null;
  editedAt: string | null;
  waivedAt: string | null;
  voidedAt: string | null;
  payKind: string | null;
  pctApplied: number | null;
  baseApplied: string | null;
  rateApplied: number | null;
  milesApplied: number | null;
  hoursApplied: number | null;
  needsHours: boolean;
  feeDisputed: boolean;
  /** Deduction lines: what is still owed on that deduction (null for a recurring one). */
  remaining: number | null;
}

export interface PendingItem {
  itemKind: 'load' | 'deduction' | 'balance_forward' | 'orphan_owner_load';
  driverId: string;
  payer: string;
  payerOwnerId: string | null;
  loadId: string | null;
  loadNumber: string | null;
  label: string;
  amount: number;
  earnedAt: string | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined ? null : String(v));

export function toDriverSettlement(r: Row): DriverSettlement {
  const status = DRIVER_SETTLEMENT_STATUSES.includes(r.status as DriverSettlementStatus) ? (r.status as DriverSettlementStatus) : 'draft';
  return {
    id: String(r.id),
    driverId: String(r.driver_id),
    driverName: str(r.driver_name) ?? '',
    payer: r.payer === 'owner' ? 'owner' : 'agency',
    payerOwnerId: str(r.payer_owner_id),
    payerName: str(r.payer_name),
    weekStart: str(r.week_start) ?? '',
    number: str(r.settlement_number) ?? '',
    status,
    loadsTotal: num(r.loads_total as string),
    deductionsTotal: num(r.deductions_total as string),
    adjustmentsTotal: num(r.adjustments_total as string),
    balanceForwardTotal: num(r.balance_forward_total as string),
    netTotal: num(r.net_total as string),
    carriedToSettlementId: str(r.carried_to_settlement_id),
    finalizedAt: str(r.finalized_at),
    canceledAt: str(r.canceled_at),
    cancelReason: str(r.cancel_reason),
    paidOn: str(r.paid_on),
    paidAt: str(r.paid_at),
    paymentMethod: str(r.payment_method),
    paymentReference: str(r.payment_reference),
  };
}

const LINE_KINDS: DriverSettlementLineKind[] = ['load', 'reimbursement', 'deduction', 'adjustment', 'balance_forward'];

export function toDriverSettlementLine(r: Row): DriverSettlementLine {
  return {
    id: String(r.id),
    settlementId: String(r.settlement_id),
    lineNo: num(r.line_no as number),
    weekStart: str(r.week_start) ?? '',
    kind: LINE_KINDS.includes(r.kind as DriverSettlementLineKind) ? (r.kind as DriverSettlementLineKind) : 'adjustment',
    loadId: str(r.load_id),
    loadNumber: str(r.load_number),
    carrierId: str(r.carrier_id),
    truckId: str(r.truck_id),
    deductionId: str(r.deduction_id),
    fromSettlementId: str(r.from_settlement_id),
    description: str(r.description) ?? '',
    amount: num(r.amount as string),
    originalAmount: numOrNull(r.original_amount as string | null),
    editedAt: str(r.edited_at),
    waivedAt: str(r.waived_at),
    voidedAt: str(r.voided_at),
    payKind: str(r.pay_kind),
    pctApplied: numOrNull(r.pct_applied as string | null),
    baseApplied: str(r.base_applied),
    rateApplied: numOrNull(r.rate_applied as string | null),
    milesApplied: numOrNull(r.miles_applied as number | null),
    hoursApplied: numOrNull(r.hours_applied as string | null),
    needsHours: r.needs_hours === true,
    feeDisputed: r.fee_disputed === true,
    remaining: numOrNull(r.remaining as string | null),
  };
}

export function toPendingItem(r: Row): PendingItem {
  return {
    itemKind: (str(r.item_kind) ?? 'load') as PendingItem['itemKind'],
    driverId: String(r.driver_id),
    payer: str(r.payer) ?? '',
    payerOwnerId: str(r.payer_owner_id),
    loadId: str(r.load_id),
    loadNumber: str(r.load_number),
    label: str(r.label) ?? '',
    amount: num(r.amount as string),
    earnedAt: str(r.earned_at),
  };
}

const asPayer = (v: unknown): DriverPayer | null => (v === 'carrier' || v === 'agency' || v === 'owner' ? v : null);

export interface DriverPayModes {
  /** The agency default (`agency_pay_settings.driver_paid_by`); null = no row = carrier. */
  agencyMode: DriverPayer | null;
  /** Per-carrier overrides (`carrier_driver_pay_settings`). */
  carrierModes: Map<string, DriverPayer>;
  /** 4 = Friday 00:00 local .. 7 = next Monday 00:00 (`settlement_week_cut_days`). */
  cutDays: number;
  /** Some settlement exists (history stays reachable after the agency goes back to carrier mode). */
  anySettlement: boolean;
  /** Some carrier is paid by the agency or an owner, or some settlement exists. */
  inUse: boolean;
}

export function useDriverPayModes(): LiveState<DriverPayModes> {
  return useLiveQuery<DriverPayModes>(async (client, agencyId) => {
    const [settings, carriers, any] = await Promise.all([
      client.from('agency_pay_settings').select('driver_paid_by, settlement_week_cut_days').eq('agency_id', agencyId),
      client.from('carrier_driver_pay_settings').select('carrier_id, driver_paid_by').eq('agency_id', agencyId),
      client.from('driver_settlements').select('id').eq('agency_id', agencyId).limit(1),
    ]);
    const s = secondary<Row>('agency_pay_settings', settings)[0];
    const carrierModes = new Map<string, DriverPayer>();
    for (const r of secondary<Row>('carrier_driver_pay_settings', carriers)) {
      const mode = asPayer(r.driver_paid_by);
      if (mode) carrierModes.set(String(r.carrier_id), mode);
    }
    const agencyMode = s ? asPayer(s.driver_paid_by) : null;
    const cut = s ? Number(s.settlement_week_cut_days) : NaN;
    const anySettlement = secondary<Row>('driver_settlements', any).length > 0;
    const inUse =
      anySettlement || (agencyMode !== null && agencyMode !== 'carrier') || [...carrierModes.values()].some((m) => m !== 'carrier');
    return { data: { agencyMode, carrierModes, cutDays: cut >= 4 && cut <= 7 ? cut : 4, anySettlement, inUse } };
  }, []);
}

/** Rows of the current agency only (every settlement read returns `agency_id`). */
const ofAgency = (agencyId: string) => (r: Row) => String(r.agency_id) === agencyId;

export interface NamedOption {
  id: string;
  name: string;
}

export interface DriverSettlementsData {
  settlements: DriverSettlement[];
  /** The week's pending items (open loads, deductions due, deficits, orphan owner loads). Secondary. */
  pending: PendingItem[];
  /** Active drivers and owners of the agency, for "Generate settlement". Secondary. */
  drivers: NamedOption[];
  owners: NamedOption[];
}

export type UseDriverSettlements = LiveState<DriverSettlementsData> & {
  /** Build or refresh one driver's settlement of a week. Returns its id. */
  generate(driverId: string, payer: SettlementPayer, ownerId: string | null, weekStart: string): Promise<ActionResult<string>>;
};

/** The settlements of every week (the page filters), and what the next build of `week` would take. */
export function useDriverSettlements(week: string | null): UseDriverSettlements {
  const query = useLiveQuery<DriverSettlementsData>(
    async (client, agencyId) => {
      const [list, pending, drivers, owners] = await Promise.all([
        client.rpc('driver_settlements_read', { p_agency: agencyId, p_week_start: null, p_status: null, p_driver: null }),
        client.rpc('driver_settlement_pending_read', { p_agency: agencyId, p_week_start: week, p_driver: null }),
        client.from('drivers').select('id, name').eq('agency_id', agencyId).eq('status', 'active').is('deleted_at', null),
        client.from('owners').select('id, legal_name').eq('agency_id', agencyId).is('deleted_at', null),
      ]);
      if (list.error) return { error: asRpcError(list.error)! };
      const byName = (a: NamedOption, b: NamedOption) => a.name.localeCompare(b.name);
      return {
        data: {
          // Filtered here too: the rows carry agency_id, so a member of two agencies never sees the other one
          // even if the server answers both (followups goal 2).
          settlements: asRows(list.data).filter(ofAgency(agencyId)).map(toDriverSettlement),
          pending: secondary<Row>('driver_settlement_pending_read', pending).filter(ofAgency(agencyId)).map(toPendingItem),
          drivers: secondary<Row>('drivers', drivers).map((r) => ({ id: String(r.id), name: String(r.name ?? '') })).sort(byName),
          owners: secondary<Row>('owners', owners).map((r) => ({ id: String(r.id), name: String(r.legal_name ?? '') })).sort(byName),
        },
      };
    },
    [week],
  );
  const { call } = useRpcAction(query.reload);
  return {
    ...query,
    generate: (driverId, payer, ownerId, weekStart) =>
      call<string>('driver_settlement_generate', {
        p_driver: driverId,
        p_payer: payer,
        p_payer_owner: payer === 'owner' ? ownerId : null,
        p_week_start: weekStart,
      }),
  };
}

/** audit-d-office (M5, 0328): a delivered load of the driver still waiting on its POD (in no settlement yet). */
export interface PendingPodLoad {
  loadId: string;
  loadNumber: string;
  deliveredAt: string;
}

export interface DriverSettlementData {
  settlement: DriverSettlement | null;
  lines: DriverSettlementLine[];
  /** On a draft: the driver's delivered loads before the week's cut with no POD yet (a secondary read). */
  pendingPod: PendingPodLoad[];
}

export type UseDriverSettlement = LiveState<DriverSettlementData> & {
  addLine(description: string, amount: number): Promise<ActionResult<string>>;
  removeLine(lineId: string, reason: string | null): Promise<ActionResult>;
  restoreLine(lineId: string): Promise<ActionResult>;
  /** A deduction line's new size, positive (the server stores it negative; W1 deviation 5). */
  setLineAmount(lineId: string, amount: number, reason: string): Promise<ActionResult>;
  setHours(loadId: string, hours: number): Promise<ActionResult>;
  finalize(): Promise<ActionResult>;
  cancel(reason: string): Promise<ActionResult>;
  markPaid(paidOn: string, method: string, reference: string | null): Promise<ActionResult>;
  unmarkPaid(reason: string): Promise<ActionResult>;
  /** Add what appeared since: generate for its own key and week. */
  refresh(): Promise<ActionResult<string>>;
};

export function useDriverSettlement(settlementId: string): UseDriverSettlement {
  const query = useLiveQuery<DriverSettlementData>(
    async (client, agencyId) => {
      const [list, lines] = await Promise.all([
        client.rpc('driver_settlements_read', { p_agency: agencyId, p_week_start: null, p_status: null, p_driver: null }),
        client.rpc('driver_settlement_lines_read', { p_settlement: settlementId }),
      ]);
      if (list.error) return { error: asRpcError(list.error)! };
      if (lines.error) return { error: asRpcError(lines.error)! };
      const settlement = asRows(list.data).map(toDriverSettlement).find((s) => s.id === settlementId) ?? null;
      // audit-d-office (M5): pay accrues at POD received, so a delivered load without its POD is in no settlement;
      // the draft says how many are waiting. Secondary: a failed read hides the line, never the page.
      let pendingPod: PendingPodLoad[] = [];
      if (settlement?.status === 'draft') {
        const pod = await client.rpc('driver_settlement_pending_pod_read', { p_week_start: settlement.weekStart, p_driver: settlement.driverId });
        pendingPod = secondary<Row>('driver_settlement_pending_pod_read', pod).map((r) => ({
          loadId: String(r.load_id),
          loadNumber: String(r.load_number ?? ''),
          deliveredAt: String(r.delivered_at ?? ''),
        }));
      }
      return {
        data: {
          settlement,
          lines: asRows(lines.data).map(toDriverSettlementLine).sort((a, b) => a.lineNo - b.lineNo),
          pendingPod,
        },
      };
    },
    [settlementId],
  );
  const { call } = useRpcAction(query.reload);
  const s = query.status === 'ready' ? query.data.settlement : null;
  return {
    ...query,
    addLine: (description, amount) => call<string>('driver_settlement_add_line', { p_settlement: settlementId, p_description: description, p_amount: amount }),
    removeLine: (lineId, reason) => call<void>('driver_settlement_remove_line', { p_line: lineId, p_reason: reason?.trim() || null }),
    restoreLine: (lineId) => call<void>('driver_settlement_restore_line', { p_line: lineId }),
    setLineAmount: (lineId, amount, reason) => call<void>('driver_settlement_set_line_amount', { p_line: lineId, p_amount: amount, p_reason: reason }),
    setHours: (loadId, hours) => call<void>('load_set_driver_hours', { p_load: loadId, p_hours: hours }),
    finalize: () => call<void>('driver_settlement_finalize', { p_settlement: settlementId }),
    cancel: (reason) => call<void>('driver_settlement_cancel', { p_settlement: settlementId, p_reason: reason }),
    markPaid: (paidOn, method, reference) =>
      call<void>('driver_settlement_mark_paid', { p_settlement: settlementId, p_paid_on: paidOn, p_method: method, p_reference: reference?.trim() || null }),
    unmarkPaid: (reason) => call<void>('driver_settlement_unmark_paid', { p_settlement: settlementId, p_reason: reason }),
    refresh: () =>
      s
        ? call<string>('driver_settlement_generate', { p_driver: s.driverId, p_payer: s.payer, p_payer_owner: s.payerOwnerId, p_week_start: s.weekStart })
        : Promise.resolve({ data: null, error: { code: undefined, message: 'settlement not loaded' } }),
  };
}
